import { useMemo } from "react";
import { useRequest } from "@/request";

/** 人员状态（对应后端 AgentStatus）。非 Active 无法进入工作台。 */
export const AgentStatus = {
  Active: 1,
  Inactive: 2,
} as const;
export type AgentStatus = (typeof AgentStatus)[keyof typeof AgentStatus];

/** 全局角色（对应后端 GlobalRole）。团队内角色见 `MembershipRole`。 */
export const GlobalRole = {
  Admin: 1,
  Viewer: 2,
  ScopeAll: 3,
} as const;
export type GlobalRole = (typeof GlobalRole)[keyof typeof GlobalRole];

export type CurrentAgentSessionDto = {
  id: string;
  iamSubject: string;
  displayName: string;
  /** IAM 登录名。邮箱在 IAM 侧允许重复，认人以它为准。 */
  userName: string;
  email: string | null;
  avatarUrl: string | null;
  status: AgentStatus;
  globalRoles: GlobalRole[];
  /** 当前有效权限集（全局角色 ∪ 各 active 团队角色），用于菜单与按钮显隐。 */
  permissions: string[];
};

/** 准入被拒时后端返回的错误码（对应后端 OrganizationErrorCodes）。 */
export const AdmissionErrorCode = {
  SubjectMissing: "Tickbird.Orgs:AgentSubjectMissing",
  NotProvisioned: "Tickbird.Orgs:AgentNotProvisioned",
  Inactive: "Tickbird.Orgs:AgentInactive",
  /** 已开通但既无全局角色也不在任何 active 组，有效权限集为空。 */
  NoRoles: "Tickbird.Orgs:AgentHasNoRoles",
} as const;
export type AdmissionErrorCode = (typeof AdmissionErrorCode)[keyof typeof AdmissionErrorCode];

/**
 * 当前后台会话（挂在 Api.Admin `session` 端点上）。
 *
 * 权限来自 tickbird 本地 RBAC，**不再向 IAM 查询**——IAM 只负责认证
 * （docs/identity-and-authz.md §4）。
 */
export function useSessionApi() {
  const { request } = useRequest();

  return useMemo(
    () => ({
      // 准入被拒时由 SessionProvider 渲染引导页，不弹 toast。
      getCurrent: () => request<CurrentAgentSessionDto>("session/current", { method: "GET", skipErrorToast: true }),
    }),
    [request],
  );
}
